/**
 * useLiveCall — the droom's customer-side two-way media lifecycle.
 *
 * Wraps `createLiveKitSession` (which maps RoomEvents onto the droom's calm
 * ConnectionState machine) and adds: local camera/mic publish, remote track
 * subscription, mute toggles, graceful teardown, and the live-call
 * capture-integrity (injection) fingerprint — one best-effort scan per distinct
 * local camera track acquisition (INTAKE-02 §2.7; see `scannedTrackIds` below).
 * Non-serialisable track handles live in React state (never the persisted
 * store).
 *
 * Demo mode (no backend → `enabled=false`): no real transport is created; the
 * toggles flip a local visual state and LiveCall shows the illustrative scene,
 * so the skeleton still renders and the build needs no live media server.
 *
 * Guarantees:
 *  - Connects to the wss URL from the backend grant (`grant.url`), never a
 *    hardcoded/env host.
 *  - Clean teardown: room disconnect stops + unpublishes the camera/mic tracks;
 *    no getUserMedia handle leaks past the call.
 *  - Camera/mic permission denial is caught and surfaced calmly; the call stays
 *    up (audio-less) rather than crashing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomEvent, Track, TrackEvent } from 'livekit-client';
import type {
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteTrack,
  RemoteVideoTrack,
  Room,
} from 'livekit-client';
import type { ConnectionState } from '@/types';
import { createLiveKitSession, onRoomSignal, type CaptureShutterSignal, fetchLiveKitToken } from '@/api';
import { isMockMode } from '@/api/client';
import { collectAndPostCaptureFingerprint } from './useCaptureIntegrity';
import { switchPublishedFacing, type FacingSwitchOutcome } from './switchFacing';
export type { FacingSwitchOutcome, FacingSwitchResult } from './switchFacing';

export interface DroomCall {
  readonly remoteVideoTrack: RemoteVideoTrack | null;
  readonly remoteAudioTrack: RemoteAudioTrack | null;
  readonly localVideoTrack: LocalVideoTrack | null;
  /**
   * CAM-2: switch the published camera.
   *
   * THREE outcomes, never a bare boolean: 'switched' (the browser confirms the lens),
   * 'unverified' (the restart worked but the browser will not describe the result — we changed
   * something and cannot prove what), 'unavailable' (a one-camera device, or the browser reports a
   * different lens). Callers must not print a lens claim on 'unverified'.
   */
  readonly switchCamera: (facing: 'user' | 'environment') => Promise<FacingSwitchOutcome>;
  readonly remoteConnected: boolean;
  /**
   * #11 (agent-drop): the VERIFIER's presence, keyed on the `agent-` identity prefix (mirror of the
   * be LiveCallRole). 'never' = no verifier has joined yet; 'present' = an agent participant is in
   * the room; 'away' = an agent was here and left. The hidden `EG_`/egress participant never counts.
   */
  readonly verifierPresence: 'never' | 'present' | 'away';
  /** ms epoch the verifier went 'away' (null when present/never). Drives the grace timer. */
  readonly verifierAwaySince: number | null;
  readonly micEnabled: boolean;
  readonly camEnabled: boolean;
  /**
   * #12 (mobile background/phone-call): distinguishes a user-intended cam-off from the SDK's
   * fallback mute. 'on' = published & live; 'off-by-user' = the customer turned it off; 'lost' =
   * intent is on but the publication is muted/missing after a background hand-off (we are
   * re-acquiring). The UI shows a calm "reconnecting your camera" note only for 'lost'.
   */
  readonly camState: 'on' | 'off-by-user' | 'lost';
  /**
   * #12: set when auto re-acquisition failed (a getUserMedia gesture is required, or audio
   * playback is blocked by autoplay policy). The UI renders a single tap target → `resumeMedia`.
   */
  readonly resumeNeeded: { cam: boolean; mic: boolean; audio: boolean } | null;
  /** #12: run the re-acquire ladder inside the customer's tap (satisfies the gesture requirement). */
  readonly resumeMedia: () => void;
  /** Calm, customer-facing camera/mic permission message (never a stack trace). */
  readonly mediaError: string | null;
  readonly toggleMic: () => void;
  readonly toggleCam: () => void;
  readonly leave: () => void;
  /**
   * V116 / owner item 4 — subscribe to SERVER-RELAYED room control signals (today: the officer's
   * remote shutter). Returns the unsubscribe.
   *
   * <p>A subscription rather than the raw `Room`, deliberately: this hook re-creates the room on an
   * in-place rejoin (§5.4), and a consumer holding the old handle would keep a listener on a dead
   * room and silently stop receiving — the shutter would work until the first reconnect and then
   * quietly not. Handlers registered here are re-attached to whatever room is current.
   */
  readonly subscribeRoomSignals: (handler: (signal: CaptureShutterSignal) => void) => () => void;
}

/** #11: identity prefix for the verifier side — must match the be LiveCallRole ('agent-'). */
const AGENT_IDENTITY_PREFIX = 'agent-';
/** #12: how long after the SDK's own 5s visibility handler we start our foreground ladder. */
const FOREGROUND_REACQUIRE_DELAY_MS = 400;
const REACQUIRE_LADDER_MS = [FOREGROUND_REACQUIRE_DELAY_MS, 2500, 6000] as const;

function isAgentIdentity(identity: string | undefined): boolean {
  return typeof identity === 'string' && identity.startsWith(AGENT_IDENTITY_PREFIX);
}

export function useLiveCall(
  sessionId: string,
  enabled: boolean,
  onState: (state: ConnectionState) => void,
  /**
   * §5.4 — bump to re-enter the SAME room IN PLACE. The connect effect depends on it, so a change tears
   * down the room and re-mints the join token WITHOUT a page navigation. Navigating instead (the previous
   * behaviour) re-mounted the app and re-ran consent, geo and the camera-permission prompt on every
   * reconnect — asking the customer to re-grant everything exactly when their connection is worst.
   */
  rejoinNonce = 0,
): DroomCall {
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<RemoteVideoTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<RemoteAudioTrack | null>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [verifierPresence, setVerifierPresence] =
    useState<'never' | 'present' | 'away'>('never');
  const [verifierAwaySince, setVerifierAwaySince] = useState<number | null>(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [camState, setCamState] = useState<'on' | 'off-by-user' | 'lost'>('on');
  const [resumeNeeded, setResumeNeeded] =
    useState<{ cam: boolean; mic: boolean; audio: boolean } | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  const roomRef = useRef<Room | null>(null);
  /**
   * V116 / item 4 — consumers subscribed to server-relayed room signals. Held in a ref (not state)
   * so registering one never re-renders the call, and so the connect effect can re-attach them to a
   * freshly created room after an in-place rejoin without the consumer knowing it happened.
   */
  const signalHandlersRef = useRef<Set<(signal: CaptureShutterSignal) => void>>(new Set());
  // #12: latest re-acquire fn (set inside the connect effect) so `resumeMedia` (a stable callback)
  // can invoke it from a user tap.
  const reacquireRef = useRef<(() => void) | null>(null);
  // #12: the customer's INTENT for each track (true = they want it on). Set on publish and on the
  // toggles; the SDK's fallback mute cannot flip these, so they are how we tell "user turned the
  // camera off" from "a phone call paused it and we must recover it".
  const camIntentRef = useRef(true);
  const micIntentRef = useRef(true);
  // #12: serialise re-acquire ladders so overlapping visibility/focus events do not stack.
  const reacquiringRef = useRef(false);
  // Keep the latest onState without re-running the connect effect on each render.
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  /**
   * INTAKE-02 §2.7 — capture-integrity (injection / virtual-camera) fingerprint on the LIVE
   * call, the PRIMARY collection surface: the plan-driven flow stopped rendering
   * DocumentCapture (DIGILOCKER_AADHAAR fills the identity slot), which left the layer-2
   * injection rail dark. Every DISTINCT local camera track acquisition — initial publish,
   * camera toggle off→on (livekit stops + re-acquires the MediaStreamTrack), device/facing
   * switch (TrackEvent.Restarted swaps it in place), reconnect republish — posts exactly one
   * scan, deduped by mediaStreamTrack.id. The persistent id set is also the StrictMode
   * double-fire guard (the useCaptureIntegrity per-track idiom): a re-run sees the same track
   * id and skips, while every real re-acquisition mints a fresh id and scans.
   */
  const scannedTrackIds = useRef<Set<string>>(new Set());
  /** LocalVideoTrack instances already instrumented with a Restarted listener (attach once). */
  const restartInstrumented = useRef<WeakSet<LocalVideoTrack>>(new WeakSet());

  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    let disposed = false;
    const lk = createLiveKitSession(
      (s) => {
        if (!disposed) onStateRef.current(s);
      },
      { adaptiveStream: true, dynacast: true },
    );
    const room = lk.room;
    roomRef.current = room;

    // V116 / item 4: re-attach the signal fan-out to THIS room. Because this lives inside the connect
    // effect, an in-place rejoin transparently re-subscribes every consumer.
    const detachSignals = onRoomSignal(room, (signal) => {
      if (disposed) return;
      for (const handler of signalHandlersRef.current) handler(signal);
    });

    // Post the injection fingerprint for a (re)acquired local camera track. BEST-EFFORT and
    // off the critical path: a collection/post failure NEVER blocks or degrades the call
    // (silent, one retry with a fresh nonce = a fresh ladder attempt); a missing scan is the
    // officer's/gate's concern server-side (honest absence, never a fabricated row) — never a
    // customer dead end. The observation wrapper stream never stops the caller's track.
    const scanLocalTrack = (track: LocalVideoTrack | undefined): void => {
      if (isMockMode()) return; // no injection rail to bind to — collect nothing (data minimisation)
      const mst = track?.mediaStreamTrack;
      // Only a live track is an honest capture surface (a toggled-off camera track is stopped).
      if (!mst || mst.readyState !== 'live') return;
      if (scannedTrackIds.current.has(mst.id)) return; // this acquisition is already scanned
      scannedTrackIds.current.add(mst.id);
      const stream = new MediaStream([mst]);
      void (async () => {
        try {
          await collectAndPostCaptureFingerprint(sessionId, stream, () => disposed);
        } catch {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (disposed || mst.readyState !== 'live') return; // nothing honest left to re-collect
          try {
            await collectAndPostCaptureFingerprint(sessionId, stream, () => disposed);
          } catch {
            // Fail-soft by design: absence is scored honestly server-side, never fabricated here.
          }
        }
      })();
    };

    const syncLocal = () => {
      if (disposed) return;
      const lp = room.localParticipant;
      const camPub = lp.getTrackPublication(Track.Source.Camera);
      const camTrack = (camPub?.videoTrack as LocalVideoTrack | undefined) ?? null;
      if (camTrack) {
        scanLocalTrack(camTrack);
        // A device/facing switch swaps the underlying MediaStreamTrack on the SAME
        // LocalVideoTrack without a room-level (un)publish — instrument the track once so the
        // restarted acquisition is fingerprinted too. Listeners go inert with the track on
        // teardown (lk.disconnect stops it); the scan guards above make late fires no-ops.
        if (!restartInstrumented.current.has(camTrack)) {
          restartInstrumented.current.add(camTrack);
          camTrack.on(TrackEvent.Restarted, () => scanLocalTrack(camTrack));
          // #12: a background hand-off ends the underlying MediaStreamTrack; the SDK may mute
          // instead of restarting. Trigger our foreground ladder so intent-on tracks recover.
          camTrack.on(TrackEvent.Ended, () => reacquireRef.current?.());
        }
      }
      setLocalVideoTrack(camTrack);
      const camOn = lp.isCameraEnabled;
      setCamEnabled(camOn);
      setMicEnabled(lp.isMicrophoneEnabled);
      // #12: derive camState from intent (not just the enabled flag). Intent-on but not enabled
      // means the publication is muted/lost — 'lost' unless the ladder is mid-recovery (which set
      // 'lost' already). A live camState:'on' clears any stale resume prompt.
      if (!camIntentRef.current) {
        setCamState('off-by-user');
      } else if (camOn) {
        setCamState('on');
      } else if (!reacquiringRef.current) {
        setCamState('lost');
      }
    };

    // #11: recompute verifier presence from the room's remote participants (never the EG_ egress).
    const syncVerifierPresence = () => {
      if (disposed) return;
      let agentHere = false;
      room.remoteParticipants.forEach((p) => {
        if (isAgentIdentity(p.identity)) agentHere = true;
      });
      const anyRemote = room.remoteParticipants.size > 0;
      setRemoteConnected(anyRemote);
      setVerifierPresence((prev) => {
        if (agentHere) {
          setVerifierAwaySince(null);
          return 'present';
        }
        // No agent in the room. If one was ever here, they are 'away'; otherwise still 'never'.
        if (prev === 'present') {
          setVerifierAwaySince((sinceValue) => sinceValue ?? Date.now());
          return 'away';
        }
        return prev;
      });
    };

    room
      .on(RoomEvent.ParticipantConnected, syncVerifierPresence)
      .on(RoomEvent.ParticipantDisconnected, () => {
        if (disposed) return;
        syncVerifierPresence();
        if (room.remoteParticipants.size === 0) {
          setRemoteVideoTrack(null);
          setRemoteAudioTrack(null);
        }
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (disposed) return;
        syncVerifierPresence();
        if (track.kind === Track.Kind.Video) setRemoteVideoTrack(track as RemoteVideoTrack);
        else if (track.kind === Track.Kind.Audio) setRemoteAudioTrack(track as RemoteAudioTrack);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (disposed) return;
        if (track.kind === Track.Kind.Video) setRemoteVideoTrack(null);
        else if (track.kind === Track.Kind.Audio) setRemoteAudioTrack(null);
      })
      .on(RoomEvent.LocalTrackPublished, syncLocal)
      .on(RoomEvent.LocalTrackUnpublished, syncLocal)
      .on(RoomEvent.TrackMuted, syncLocal)
      .on(RoomEvent.TrackUnmuted, syncLocal)
      // Re-sync (and fingerprint any re-acquired camera track) after a full reconnect
      // republish and after an active-device switch — both can mint a new MediaStreamTrack
      // without a fresh LocalTrackPublished; a resume that kept the same live track is the
      // same acquisition and dedupes to no-op.
      .on(RoomEvent.Reconnected, () => {
        syncLocal();
        syncVerifierPresence();
        // A reconnect can land with the camera muted (background hand-off during the drop).
        reacquireRef.current?.();
      })
      .on(RoomEvent.ActiveDeviceChanged, syncLocal);

    const run = async () => {
      try {
        const grant = await fetchLiveKitToken(sessionId);
        if (disposed) return;
        await lk.connect(grant); // connects to grant.url from the API
        if (disposed) return;
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
          await room.localParticipant.setCameraEnabled(true);
          if (!disposed) {
            setMediaError(null);
            camIntentRef.current = true;
            micIntentRef.current = true;
            syncLocal();
            // The verifier may already be in the room when we join (they warm-handoff first).
            syncVerifierPresence();
          }
        } catch (mediaErr) {
          if (!disposed) {
            // Only a real getUserMedia denial means "allow access" — those come back as
            // a DOMException with a specific name. A publish that fails for another
            // reason (e.g. the media server can't confirm the track) isn't a permission
            // problem, so don't tell the customer to change browser settings.
            const name = (mediaErr as { name?: string } | undefined)?.name;
            const isPermissionDenied =
              name === 'NotAllowedError' ||
              name === 'NotFoundError' ||
              name === 'NotReadableError' ||
              name === 'OverconstrainedError' ||
              name === 'SecurityError';
            setMediaError(
              isPermissionDenied
                ? "We can't reach your camera or microphone. Please allow access in your browser, then rejoin."
                : "We're having trouble connecting your camera and microphone. Please retry in a moment.",
            );
          }
        }
      } catch {
        // API/connect failure → calm, resumable state (never a hard error screen).
        if (!disposed) onStateRef.current('suspended');
      }
    };
    void run();

    // #12 — recover camera/mic/audio after a background hand-off (a phone call, tab switch, or
    // bfcache restore). livekit-client 2.20.0 gates its own foreground re-acquire on `!isMuted`,
    // and its failure fallback MUTES the track — so a phone call leaves the camera muted with no
    // automatic recovery. We watch for the customer returning and re-enable any track whose INTENT
    // is on but whose publication is muted/dead, running a short retry ladder. On final failure we
    // surface `resumeNeeded` so the UI can offer a tap (a getUserMedia gesture may be required).
    const reacquireIfNeeded = (): void => {
      if (disposed || reacquiringRef.current) return;
      const lp = room.localParticipant;
      const camPub = lp.getTrackPublication(Track.Source.Camera);
      const micPub = lp.getTrackPublication(Track.Source.Microphone);
      const camTrack = camPub?.track;
      const micTrack = micPub?.track;
      const camNeeds =
        camIntentRef.current &&
        (!camPub ||
          camPub.isMuted ||
          !camTrack ||
          camTrack.mediaStreamTrack?.readyState !== 'live' ||
          camTrack.mediaStreamTrack?.muted === true);
      const micNeeds =
        micIntentRef.current &&
        (!micPub ||
          micPub.isMuted ||
          !micTrack ||
          micTrack.mediaStreamTrack?.readyState !== 'live' ||
          micTrack.mediaStreamTrack?.muted === true);
      const audioNeeds = !room.canPlaybackAudio;
      if (!camNeeds && !micNeeds && !audioNeeds) {
        setResumeNeeded(null);
        return;
      }
      if (camNeeds) setCamState('lost');
      reacquiringRef.current = true;
      let attempt = 0;
      const tryOnce = async (): Promise<boolean> => {
        let ok = true;
        try {
          if (audioNeeds && !room.canPlaybackAudio) await room.startAudio();
        } catch {
          ok = false;
        }
        try {
          if (camIntentRef.current && camNeeds) await lp.setCameraEnabled(true);
        } catch {
          ok = false;
        }
        try {
          if (micIntentRef.current && micNeeds) await lp.setMicrophoneEnabled(true);
        } catch {
          ok = false;
        }
        return ok;
      };
      const ladder = (): void => {
        if (disposed) {
          reacquiringRef.current = false;
          return;
        }
        void tryOnce().then((ok) => {
          syncLocal();
          const stillCam = camIntentRef.current && !room.localParticipant.isCameraEnabled;
          const stillMic = micIntentRef.current && !room.localParticipant.isMicrophoneEnabled;
          const stillAudio = !room.canPlaybackAudio;
          const settled = ok && !stillCam && !stillMic && !stillAudio;
          if (settled || disposed) {
            reacquiringRef.current = false;
            if (settled) {
              setResumeNeeded(null);
              if (camIntentRef.current) setCamState('on');
            }
            return;
          }
          attempt += 1;
          if (attempt < REACQUIRE_LADDER_MS.length) {
            window.setTimeout(ladder, REACQUIRE_LADDER_MS[attempt]);
          } else {
            // Ladder exhausted → ask for a tap (gesture-required recovery / blocked autoplay).
            reacquiringRef.current = false;
            setResumeNeeded({ cam: stillCam, mic: stillMic, audio: stillAudio });
          }
        });
      };
      window.setTimeout(ladder, REACQUIRE_LADDER_MS[0]);
    };
    reacquireRef.current = reacquireIfNeeded;

    const onVisible = () => {
      if (document.visibilityState === 'visible') reacquireIfNeeded();
    };
    const onPageShow = () => reacquireIfNeeded();
    const onFocus = () => reacquireIfNeeded();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);

    return () => {
      disposed = true;
      reacquireRef.current = null;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      detachSignals();
      room.removeAllListeners();
      lk.disconnect(); // stops + unpublishes local camera/mic (no leaked media)
      roomRef.current = null;
    };
  }, [sessionId, enabled, rejoinNonce]);

  const subscribeRoomSignals = useCallback(
    (handler: (signal: CaptureShutterSignal) => void): (() => void) => {
      signalHandlersRef.current.add(handler);
      return () => {
        signalHandlersRef.current.delete(handler);
      };
    },
    [],
  );

  const toggleMic = useCallback(() => {
    const room = roomRef.current;
    if (room) {
      const next = !room.localParticipant.isMicrophoneEnabled;
      micIntentRef.current = next; // #12: record the customer's intent
      void room.localParticipant
        .setMicrophoneEnabled(next)
        .then(() => setMicEnabled(next))
        .catch(() => undefined);
    } else {
      micIntentRef.current = !micIntentRef.current;
      setMicEnabled((v) => !v); // demo-mode visual toggle
    }
  }, []);

  const toggleCam = useCallback(() => {
    const room = roomRef.current;
    if (room) {
      const next = !room.localParticipant.isCameraEnabled;
      camIntentRef.current = next; // #12: record the customer's intent
      setCamState(next ? 'on' : 'off-by-user');
      void room.localParticipant
        .setCameraEnabled(next)
        .then(() => {
          const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
          setLocalVideoTrack((camPub?.videoTrack as LocalVideoTrack | undefined) ?? null);
          setCamEnabled(next);
        })
        .catch(() => undefined);
    } else {
      camIntentRef.current = !camIntentRef.current;
      setCamState(camIntentRef.current ? 'on' : 'off-by-user');
      setCamEnabled((v) => !v); // demo-mode visual toggle
    }
  }, []);

  // #12: run the re-acquire ladder from a user gesture (the tap-to-resume affordance).
  const resumeMedia = useCallback(() => {
    setResumeNeeded(null);
    reacquireRef.current?.();
  }, []);

  /**
   * CAM-2..CAM-5 (KYCID·00001-00006-00002): switch the PUBLISHED camera to `facing`.
   *
   * This RESTARTS the published LiveKit camera track on the requested lens (see switchFacing.ts for
   * the ladder, and for why restartTrack rather than switchActiveDevice), so it changes what the
   * recording contains mid-call — an evidence event, not a UI toggle. It is deliberately here and
   * not in GuidedCaptureOverlay: the overlay must never call getUserMedia (a second acquisition can
   * STEAL the camera on iOS Safari and black out the officer's feed), so it asks, and the hook that
   * owns the room acts.
   *
   * Never throws, and never leaves the camera stopped: a device that cannot give us the requested
   * lens keeps the one it has, and the capture completes on it (CAM-5).
   */
  const switchCamera = useCallback(
    async (facing: 'user' | 'environment'): Promise<FacingSwitchOutcome> => {
      const room = roomRef.current;
      if (!room) return { result: 'unavailable', observed: null };
      const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const videoTrack = camPub?.videoTrack as LocalVideoTrack | undefined;
      const outcome = await switchPublishedFacing(videoTrack ?? null, facing);
      // The publication object is unchanged (restartTrack swaps the MediaStreamTrack IN place and
      // livekit re-attaches every attached element itself), so this is a no-op re-set kept only so
      // the hook's contract stays "state reflects the current track" for any future consumer that
      // keys off identity rather than the element attachment.
      setLocalVideoTrack(videoTrack ?? null);
      return outcome;
    },
    [],
  );

  const leave = useCallback(() => {
    roomRef.current?.disconnect();
  }, []);

  return {
    remoteVideoTrack,
    remoteAudioTrack,
    localVideoTrack,
    remoteConnected,
    verifierPresence,
    verifierAwaySince,
    micEnabled,
    camEnabled,
    camState,
    resumeNeeded,
    resumeMedia,
    mediaError,
    toggleMic,
    toggleCam,
    switchCamera,
    leave,
    subscribeRoomSignals,
  };
}
